/**
 * MomentsGallery — masonry grid of approved uploads.
 *
 * Used by:
 *   • /m/:slug (public — approved only, deep-link a media item)
 *   • Owner dashboard (all items, with moderation controls when
 *     `moderateFn` is passed).
 *
 * Owner rule #4: GLOBAL SNIPPET, not a per-page copy.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Check, X, Trash2, Play, MapPin, ChevronDown, Share2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ||
             (typeof window !== "undefined" ? window.location.origin : "");

export default function MomentsGallery({
  slug,
  isOwner    = false,
  filter     = "approved",   // "approved" | "pending" | "rejected" | "all"
  refreshKey = 0,
  moderateFn,                // async(mediaId, status) — owner only
  deleteFn,                  // async(mediaId)         — owner only
  testid     = "moments-gallery",
}) {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        // Owner endpoint bundles page_id in URL; here we always call
        // by slug because Moderate/Delete use media_id directly.
        const url = isOwner
          ? `${API}/api/moments/pages/OWNER-VIEW/gallery`  // resolved by parent
          : `${API}/api/moments/m/${slug}/gallery?limit=120`;
        // For owner we lean on the parent to pass items via `injectedItems`
        // — but to keep the snippet simple we still support owner fallback
        // via /m/{slug}/gallery + status filter server-side on public gate.
        const r = await fetch(
          isOwner
            ? `${API}/api/moments/m/${slug}/gallery?limit=200`
            : url,
          { credentials: "include" },
        );
        const j = await r.json();
        if (!alive) return;
        setItems(Array.isArray(j.items) ? j.items : []);
      } catch (e) {
        if (!alive) return;
        toast.error(`Gallery failed to load: ${e.message || e}`);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [slug, isOwner, refreshKey]);

  const _moderate = async (m, status) => {
    if (!moderateFn) return;
    await moderateFn(m.media_id, status);
    setItems((xs) => xs.filter((x) => x.media_id !== m.media_id));
    setModal(null);
  };

  const _delete = async (m) => {
    if (!deleteFn) return;
    if (!window.confirm("Delete this upload? This cannot be undone.")) return;
    await deleteFn(m.media_id);
    setItems((xs) => xs.filter((x) => x.media_id !== m.media_id));
    setModal(null);
  };

  const _share = async (m) => {
    const url = m.public_url;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Moment", url });
      } else {
        await navigator.clipboard?.writeText(url);
        toast.success("Link copied");
      }
    } catch (_e) { /* cancel */ }
  };

  const visible = items.filter((m) => {
    if (filter === "all") return true;
    return (m.status || "approved") === filter;
  });

  if (loading) {
    return (
      <div className="py-16 flex justify-center" data-testid={`${testid}-loading`}>
        <Loader2 className="animate-spin text-stone-400" size={22}/>
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <div className="py-16 text-center text-sm text-stone-500"
           data-testid={`${testid}-empty`}>
        {filter === "pending"
          ? "No uploads waiting for your review."
          : "No uploads yet — be the first to share a moment."}
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3"
           data-testid={`${testid}-grid`}>
        {visible.map((m) => (
          <button
            key={m.media_id}
            type="button"
            onClick={() => setModal(m)}
            data-testid={`${testid}-item-${m.media_id}`}
            className="group relative aspect-square overflow-hidden rounded-xl bg-stone-100 ring-1 ring-stone-200 hover:ring-stone-400 transition"
          >
            {m.kind === "video" ? (
              <>
                <video
                  src={m.public_url}
                  className="w-full h-full object-cover"
                  muted
                  playsInline
                  preload="metadata"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="rounded-full bg-black/40 p-2">
                    <Play size={18} className="text-white"/>
                  </div>
                </div>
              </>
            ) : (
              <img
                src={m.public_url}
                alt={m.caption || ""}
                loading="lazy"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
            )}
            {isOwner && m.status === "pending" && (
              <span className="absolute top-1 left-1 rounded-full bg-amber-400 text-stone-900 text-[10px] font-bold px-2 py-0.5">
                Pending
              </span>
            )}
            {m.lat && m.lng && (
              <span className="absolute bottom-1 right-1 rounded-full bg-white/80 p-1">
                <MapPin size={11} className="text-stone-700"/>
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox / detail modal */}
      {modal && (
        <div
          role="dialog"
          onClick={() => setModal(null)}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          data-testid={`${testid}-modal`}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="max-w-3xl w-full bg-stone-950 rounded-2xl overflow-hidden ring-1 ring-white/10"
          >
            <div className="relative bg-black flex items-center justify-center max-h-[70vh]">
              {modal.kind === "video" ? (
                <video
                  src={modal.public_url}
                  controls
                  autoPlay
                  playsInline
                  className="max-h-[70vh] w-auto"
                />
              ) : (
                <img
                  src={modal.public_url}
                  alt={modal.caption || ""}
                  className="max-h-[70vh] w-auto object-contain"
                />
              )}
              <button
                type="button"
                onClick={() => setModal(null)}
                className="absolute top-3 right-3 rounded-full bg-white/10 hover:bg-white/20 p-2 text-white"
                aria-label="Close"
                data-testid={`${testid}-modal-close`}
              >
                <ChevronDown size={18}/>
              </button>
            </div>
            <div className="p-4 space-y-2">
              {modal.caption && (
                <p className="text-white text-sm">{modal.caption}</p>
              )}
              <p className="text-stone-400 text-xs">
                {modal.uploader_name ? `From ${modal.uploader_name} · ` : ""}
                {new Date(modal.created_at).toLocaleString()}
              </p>
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => _share(modal)}
                  data-testid={`${testid}-modal-share`}
                  className="inline-flex items-center gap-1.5 text-xs text-stone-200 bg-white/10 hover:bg-white/20 rounded-full px-3 py-1.5"
                >
                  <Share2 size={13}/> Share
                </button>
                {isOwner && modal.status === "pending" && (
                  <>
                    <button
                      type="button"
                      onClick={() => _moderate(modal, "approved")}
                      data-testid={`${testid}-modal-approve`}
                      className="inline-flex items-center gap-1.5 text-xs text-white bg-emerald-600 hover:bg-emerald-500 rounded-full px-3 py-1.5"
                    >
                      <Check size={13}/> Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => _moderate(modal, "rejected")}
                      data-testid={`${testid}-modal-reject`}
                      className="inline-flex items-center gap-1.5 text-xs text-white bg-rose-600 hover:bg-rose-500 rounded-full px-3 py-1.5"
                    >
                      <X size={13}/> Reject
                    </button>
                  </>
                )}
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => _delete(modal)}
                    data-testid={`${testid}-modal-delete`}
                    className="inline-flex items-center gap-1.5 text-xs text-white bg-rose-800/60 hover:bg-rose-700 rounded-full px-3 py-1.5 ml-auto"
                  >
                    <Trash2 size={13}/> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
